import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { every, flatten, isEmpty, toLower, values, words } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { RouteComponentProps } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import { getLabelColorStyle } from "../../../../components/labels/utils";
import { useAppearance } from "../../../../context/appearance";
import { useLabels } from "../../../../context/labels";
import {
  LoadingKey,
  useLoading
} from "../../../../context/loading/LoadingProvider";
import classNames from "../../../../utils/classNames";
import { Entity } from "../../../../utils/constants";
import { Label } from "../../../../utils/types";
import useDebounce from "../../../../utils/useDebounce";
import EntityDataTable from "../../common/EntityDataTable";
import TablePropertiesSidebar from "../../common/TablePropertiesSidebar";
import TableSearchInput from "../../common/TableSearchInput";
import useDataTable, { Property } from "../../common/useDataTable";
import { PaginationKey } from "../../common/usePagination";
import LabelActionsButton from "./LabelActionsButton";
import LabelFormSidebar from "./LabelFormSidebar";

export interface Props extends RouteComponentProps<{}> {}

const LabelsPage: React.FC<Props> = () => {
  const { getIsReady } = useLoading();
  const { isDark } = useAppearance();
  const { labels, linkedLabels, fetchLabels } = useLabels();
  const searchInput = useDataTable((state) => state.searchInput[Entity.label]);
  const debouncedSearchInput = useDebounce(searchInput, 200);
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const [openLabelFormSidebar, setOpenLabelFormSidebar] = useState(false);
  const [currentlyEditedLabel, setCurrentlyEditedLabel] = useState<
    Label | undefined
  >();

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const toggleEditLabelForm = useCallback(
    (label: Label | undefined) => {
      if (openLabelFormSidebar) {
        setCurrentlyEditedLabel(undefined);
        setOpenLabelFormSidebar(false);
      } else {
        setCurrentlyEditedLabel(label);
        setOpenLabelFormSidebar(true);
      }
    },
    [openLabelFormSidebar]
  );

  const filteredLabels = useMemo(() => {
    let filteredList = values(labels).filter((label) => {
      if (debouncedSearchInput) {
        const searchInputWords = words(debouncedSearchInput).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(label.name).includes(word) || label.type.includes(word)
        );
      }
      return true;
    });

    return filteredList;
  }, [labels, debouncedSearchInput]);

  const properties: { [key: string]: Property<Label> } = useMemo(
    () => ({
      name: {
        key: "name",
        title: "Name",
        renderContent: (label) => (
          <div
            className="inline-flex items-center rounded-full font-medium px-3 py-0.5 my-2 text-sm"
            style={getLabelColorStyle(label.color, isDark)}
          >
            {label.name}
          </div>
        )
      },
      type: {
        key: "type",
        title: "Type",
        renderContent: (label) => label.type
      },
      usage: {
        key: "usage",
        title: "Usage",
        renderContent: (label) => {
          const linkedItemsCount = flatten(values(linkedLabels) as any).filter(
            (labelUuid) => labelUuid === label.uuid
          ).length;
          if (!linkedItemsCount) return null;
          return linkedItemsCount;
        }
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (label) => (
          <LabelActionsButton
            label={label}
            toggleEditLabelForm={toggleEditLabelForm}
          />
        )
      }
    }),
    [isDark, linkedLabels, toggleEditLabelForm]
  );

  const isLoadingLabels = useMemo(
    () => isEmpty(labels) && !getIsReady([LoadingKey.workspaceLabels]),
    [getIsReady, labels]
  );

  const renderLabelsTable = useMemo(() => {
    if (isLoadingLabels) {
      return <CenteredLoading className="h-96" />;
    }

    return (
      <>
        <div className="flex items-center justify-between pb-4 mt-6">
          <div className="w-7/12">
            <TableSearchInput
              entity={Entity.label}
              placeholder="Search a label by name..."
            />
          </div>

          <div className="flex items-center w-5/12 justify-end">
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <div>
                    <Menu.Button className="p-2 rounded-full bg-white flex items-center text-gray-400 hover:text-gray-600 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700">
                      <span className="sr-only">Open options</span>
                      <DotsVerticalIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    show={open}
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items
                      static
                      className="z-50 origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
                    >
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => {
                                setShowPropertiesSidebar(true);
                              }}
                              className={classNames(
                                active
                                  ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                  : "text-gray-700 dark:bg-gray-800 dark:text-white",
                                "w-full flex justify-between px-4 py-2 text-sm"
                              )}
                            >
                              <span>Properties</span>
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>

            <button
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={() => setOpenLabelFormSidebar(true)}
            >
              Add label
            </button>
          </div>
        </div>

        <EntityDataTable<Label>
          paginationKey={PaginationKey.settingsLabels}
          entity={Entity.label}
          items={filteredLabels}
          properties={properties}
        />
      </>
    );
  }, [filteredLabels, isLoadingLabels, properties]);

  return (
    <>
      <LabelFormSidebar
        label={currentlyEditedLabel}
        open={openLabelFormSidebar}
        toggleEditLabelForm={toggleEditLabelForm}
      />

      <TablePropertiesSidebar<Label>
        entity={Entity.label}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      <div className="flex flex-col w-full">
        <div>
          <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Workspace labels
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            Configure your labels here
          </p>
        </div>

        {renderLabelsTable}
      </div>
    </>
  );
};

export default LabelsPage;
